import * as _ from 'underscore';
import { CompositeSessionInfoSelectors } from '../../app/selectors/SessionInfoSelectors';
import { CommonDatasourceInfo } from '../../app/spaces/CommonDatasourceInfo';
import { AppState } from '../../app/state/AppState';
import { TextBoxMixedValues } from '../directives/tbTextBox';
import { ListResult } from '../fetchers/ListResult';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode, DataConnectionErrorCode } from '../helpers/ErrorCodes';
import { Filter } from '../helpers/Filter';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { L10n } from '../L10n/L10n';
import { RemoteConnectionDetailInfo } from '../models/ConnectionDetailInfo';
import { IDataConnectionInfo, IDataConnectionInfoProperties } from '../models/IDataConnectionInfo';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { IUserInfo } from '../models/IUserInfo';
import { IIdItemInfo, IOwnedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import {
  DataConnections,
  IConnectionUpdate,
  IEmbeddedPasswordUpdate
} from '../resources/DataConnections';
import { DataSources } from '../../content/resources/Datasources';
import { OAuthCredentials } from '../resources/OAuthCredentials';
import { ResourceQuery } from '../resources/ResourceQuery';
import { ICheckConnectionResult } from '../ServerApi/requests/CheckConnectionRequest';
import { IGetCommonUserOAuthCredentialResult } from '../ServerApi/requests/GetCommonUserOAuthCredentialRequest';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IOAuthCredential, IOAuthCredentialTypeInfo } from '../ServerApi/types/IOAuthCredential';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { AddOAuthCredentialAction } from './AddOAuthCredentialAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ContentActionNotification } from '../../commonInfra/contentActions/ContentActionNotification';
import { ModalSize } from './ModalService';
import { IOAuthCredentialsService } from './OAuthCredentialsService';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { openReactAlertDialog } from '../../commonInfra/dialogs/ReactAlertDialog';
import { Messages } from '../../codegen/Localize';

export interface IEditConnectionDialogScope extends ng.IScope {
  prompt: string;
  editModel: IDataConnectionEditModel;
  canEmbedPassword: boolean;
  canSessionUserEditEmbeddedCredentials: boolean;
  showTestConnection: boolean;
  testConnection: () => void;
  testConnectionResult: ITestConnectionResult;
  usingRemoteQueryAgent: boolean;
  showNetworkType: boolean;

  editConnectionForm: ng.IFormController;
}

export interface IEditOAuthConnectionDialogScope extends ng.IScope {
  prompt?: string;
  withAuthenticationSubHeader?: boolean;
  promptEmbedCredential: string;
  promptNoCredential: string;
  describeEmbedCredential: string;
  describeNoCredential: string;

  connectionTypeDisplayName: string;
  credentials: IOAuthCredential[];
  addNewOAuthCredentialOptionValue?: string;
  oauthEditModel: IOAuthConnectionEditModel;

  allowOAuthWithPassword: boolean;

  showTestConnection: boolean;
  usingRemoteQueryAgent: boolean;
  showNetworkType: boolean;
  testConnectionDisabled: () => boolean;
  testConnection: () => void;
  testConnectionResult: ITestConnectionResult;
}

// A sentinel credential id to mean "Add new OAuth credential"
const AddNewOAuthCredentialOptionId: string = '__ADD_NEW_OAUTH_CREDENTIAL';

export let PasswordUnchanged: string = null;

export interface IDataConnectionEditModel {
  connectionType? : string;
  serverName?: string;
  serverPort?: string;
  username?: string;
  password?: IEmbeddedPasswordUpdate;
  usingRemoteQueryAgent?: boolean;
}

export type CredentialOption = 'none' | 'oauth' | 'password' | 'mixed';

export interface IOAuthConnectionEditModel {
  credentialOption: CredentialOption;
  credentialId?: string;
  credentialNoValueOptionText?: string; // this will be set if there is an extra null credential option

  usernamePassword?: IDataConnectionEditModel;  // For Salesforce
}

const DialogReserveHeight = 450;

export interface ITestConnectionResult {
  isPending: boolean;
  succeeded?: boolean;
  messages?: string[];
}

export type EditableConnection = IDataConnectionInfoProperties & IIdItemInfo;

export module ConnectionEditabilityHelper {

  export function isConnectionEditable(connection: EditableConnection) {
    return connection.connectionTypeEditable;
  }

  function getConnectionOwner(connection: IDataConnectionInfo): IUserInfo | null {
    const ownedItem: IOwnedItemInfo = connection.datasource ? connection.datasource : connection.flow;
    return ownedItem ? ownedItem.owner : null;
  }

  export function userCanEditConnection(connection: IDataConnectionInfo, state: AppState): boolean {
    const owner = getConnectionOwner(connection);
    const isAdminOrOwner = CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(owner)(state);
    return isConnectionEditable(connection) && isAdminOrOwner;
  }

  export function userCanEditDatasource(datasource: CommonDatasourceInfo, state: AppState): boolean {
    // The frontend doesn't always know how many connections are in the data source, so we'll say
    // that if the user owns the data source, we'll allow them to attempt to edit. Once the user
    // selects the action, we call the backend to get all the associated connections, and then we know
    // for sure whether or not the data source is actually editable, and we can display a specific
    // error dialog if it's not.
    return CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(datasource.owner)(state);
  }
}

export class EditConnectionAction {

  public static SalesforceConnectionType = 'salesforce'; // Maybe move to ServerApi

  public static $inject = [
    'ConfirmActionDialog',
    '$translate',
    'DataConnections',
    'AddOAuthCredentialAction',
    'OAuthCredentialsService'
  ];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private DataConnections: DataConnections,
    private AddOAuthCredentialAction: AddOAuthCredentialAction,
    private oAuthCredentialsService: IOAuthCredentialsService) {
  }

  public confirmEditConnections(connections: EditableConnection[], context: IActionContext): ng.IPromise<any> {

    if (!this.haveSameConnectionType(connections)) {
      return openReactAlertDialog({
        title: Messages.editDataConnections_mixedConnectionTypesTitle(),
        message: Messages.editDataConnections_mixedConnectionTypesMessage()
      }).result;
    }

    if (!_.all(connections, ConnectionEditabilityHelper.isConnectionEditable)) {
      return openReactAlertDialog({
        title: Messages.editDataConnections_nonEditableTitle(),
        message: Messages.editDataConnections_nonEditableMessage()
      }).result;
    }

    if (connections[0].connectionOAuth) {
      return this.confirmEditOAuthConnections(connections, context);
    } else {
      return this.confirmEditDatabaseConnections(connections, context);
    }
  }

  private haveSameConnectionType(connections: EditableConnection[]) {
    const firstConnectionType = connections[0].connectionType;
    return _.all(connections, c => c.connectionType === firstConnectionType);
  }

  private confirmEditDatabaseConnections(connections: EditableConnection[], context: IActionContext): ng.IPromise<any> {

    const dialogScope = <IEditConnectionDialogScope>context.scope.$new();
    const connectionsDetails: RemoteConnectionDetailInfo[] =
        _.map(connections, c => <RemoteConnectionDetailInfo> c.connectionDetails);

    dialogScope.prompt = this.makePrompt(connections);

    dialogScope.editModel = {
      connectionType : connections.length === 1 ? connections[0].connectionType : null,
      serverName: this.uniqueValueOrMixed(connectionsDetails, cd => cd.serverName),
      serverPort: this.uniqueValueOrMixed(connectionsDetails, cd => cd.serverPort),
      username: this.uniqueValueOrMixed(connectionsDetails, cd => cd.databaseUsername),
      password: {
        isEmbedded: this.uniqueValueOrUndefined(connectionsDetails, cd => cd.hasEmbeddedPassword),
        newPassword: PasswordUnchanged
      }
    };
    dialogScope.canSessionUserEditEmbeddedCredentials = getSessionManagementServiceInstance().canSessionUserEditEmbeddedCredentials();

    // Determine whether the password can be embedded. Currently published cube datasources don't allow
    // users to embed passwords in 'Edit Connection' dialog.
    const datasource: IDataSourceInfo = <IDataSourceInfo>((<IDataConnectionInfo>connections[0]).datasource);
    dialogScope.canEmbedPassword = true;
    if (DataSources.isPublished(datasource)){
      this.checkCubeDatasource(datasource).then((isCubeDatasource) => {
        dialogScope.canEmbedPassword = !isCubeDatasource;
        // There was a problem with Angular updating the form. Could be due to it being inside React.
        dialogScope.$applyAsync();
      });
    }

    // Determine if test connection button is displayed to customers, as of Q4 2016, we disable them for
    // generic-auth-based WDC credentials. TFS 546010
    dialogScope.showTestConnection = connections.length === 1 && connections[0].testingConnectionAllowed;
    dialogScope.testConnection = () => {
      dialogScope.testConnectionResult = this.checkEditedConnection(connections[0].id, dialogScope.editModel);
    };
    dialogScope.editModel.usingRemoteQueryAgent = connections.length >= 1 &&
      connections[0].usingRemoteQueryAgent;
    dialogScope.showNetworkType = this.showNetworkType(datasource);

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: dialogScope,
      title: this.$translate.instant('editConnection'),
      bodyTemplateUrl: 'editConnectionDialog.html',
      position: { reserveHeight: DialogReserveHeight },
      actionName: this.$translate.instant('save'),
      action: (dialogInstance: IConfirmActionDialogInstance) =>
        this.applyConnectionEdits(connections, dialogScope.editModel)
    });

    dialogInstance.result.finally(() => dialogScope.$destroy());
    return dialogInstance.result;
  }

  private checkCubeDatasource = async (datasource: IDataSourceInfo) => {
    try{
      const datasourceDetailInfo: IDataSourceDetailInfo = await DataSources.getById(datasource.id);
      return datasourceDetailInfo.isHierarchical;
    }
    catch(e){
      return false;
    }
  }

  private makePrompt(connections: EditableConnection[]) {
    return this.$translate.instant('editDataConnections_prompt', {
      ITEM_COUNT: connections.length
    }, L10n.Interpolations.Count);
  }

  public configureEditOAuthScope(scope: IEditOAuthConnectionDialogScope, connections: EditableConnection[]) {
    const connectionType = connections[0].connectionType;
    const connectionDetails = connections[0].connectionDetails;
    let instanceUrl: string;
    let role: string;
    if (connectionDetails.type === 'database') {
      instanceUrl = connectionDetails.instanceUrl;
      role = connectionDetails.role;
    }

    const oAuthCredentialTypeInfo: IOAuthCredentialTypeInfo = this.oAuthCredentialsService.getTypeInfo(connectionType);

    scope.allowOAuthWithPassword = connectionType === EditConnectionAction.SalesforceConnectionType;

    // Prompts
    const displayStrings = this.oAuthCredentialsService.getDisplayStrings(connectionType);
    scope.promptEmbedCredential = displayStrings.promptEmbedCredential;
    scope.promptNoCredential = displayStrings.noCredential;
    scope.describeEmbedCredential = displayStrings.describeEmbedCredential;
    scope.describeNoCredential = displayStrings.describeNoCredential;

    scope.connectionTypeDisplayName = connections[0].connectionTypeDisplayName;
    scope.oauthEditModel = this.createOAuthEditModel(connections);

    const refreshCredentials = () => {
      const credentialsQuery: ResourceQuery<IOAuthCredential> =
        OAuthCredentials.all().addFilter(Filter.fieldEq('type', connectionType));

      return SliceableHelper.fetchAll(credentialsQuery)
        .then((credentialsListResult: ListResult<IOAuthCredential>) => {
          scope.credentials = credentialsListResult.items;
          return credentialsListResult; // For chaining
        });
    };
    refreshCredentials();

    if(OAuthCredentials.canAddForConnectionType(oAuthCredentialTypeInfo)) {
      scope.addNewOAuthCredentialOptionValue = AddNewOAuthCredentialOptionId;
    }

    scope.$watch(() => scope.oauthEditModel.credentialId, (credentialId: string) => {
      if (credentialId === AddNewOAuthCredentialOptionId) {
        scope.oauthEditModel.credentialId = undefined;

        this.AddOAuthCredentialAction.attemptAuth(connectionType, instanceUrl, role).then((credentialId: string) => {
          refreshCredentials().then((credentialsListResult: ListResult<IOAuthCredential>) => {
            // Set the credentialId if it exists in the list
            if(_.find(credentialsListResult.items, (credential: IOAuthCredential) => credential.id === credentialId)) {
              scope.oauthEditModel.credentialId = credentialId;
            }
          });
        });
      }
    });

    // Determine if test connection button is displayed to customers, as of Q4 2016, we disable them for
    // generic-auth-based WDC credentials. TFS 546010
    scope.showTestConnection = connections.length === 1
      && this.oAuthCredentialsService.supportsTestConnection(connections[0].connectionType)
      && connections[0].testingConnectionAllowed;
    scope.testConnection = () => {
      scope.testConnectionResult =
        scope.oauthEditModel.credentialOption === 'password'
          ? this.checkEditedConnection(connections[0].id, scope.oauthEditModel.usernamePassword)
          : this.checkOAuthConnection(scope.oauthEditModel.credentialId);
    };
    scope.showNetworkType = false;

    scope.testConnectionDisabled = () => {
      // Short-circut if testConnection is already disabled
      if (!scope.showTestConnection) return true;

      const editModel = scope.oauthEditModel;
      const credentialOption = editModel.credentialOption;
      return scope.testConnectionResult && scope.testConnectionResult.isPending
          || editModel.credentialOption === 'mixed'
          || (credentialOption === 'password' && !editModel.usernamePassword.username)
          || (credentialOption === 'oauth' && !editModel.credentialId)
          || (credentialOption === 'none');
    };
  }

  private confirmEditOAuthConnections(connections: EditableConnection[], context: IActionContext) {
    const dialogScope = <IEditOAuthConnectionDialogScope>context.scope.$new();
    this.configureEditOAuthScope(dialogScope, connections);

    dialogScope.prompt = this.makePrompt(connections);
    dialogScope.withAuthenticationSubHeader = true;

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: dialogScope,
      title: this.$translate.instant('editConnection'),
      size: ModalSize.medium,
      bodyTemplateUrl: dialogScope.allowOAuthWithPassword ? 'editSalesforceConnectionDialog.html' : 'editOAuthConnectionDialog.html',
      position: { reserveHeight: DialogReserveHeight },
      actionName: this.$translate.instant('save'),
      action: (dialogInstance: IConfirmActionDialogInstance) =>
        this.updateOAuthConnections(connections, dialogScope.oauthEditModel)
    });

    dialogInstance.result.finally(() => dialogScope.$destroy());
    return dialogInstance.result;
  }

  public createOAuthEditModel(connections: EditableConnection[]): IOAuthConnectionEditModel {

    const editModel: IOAuthConnectionEditModel = {
      credentialOption: 'mixed',
      credentialId: undefined,
      credentialNoValueOptionText: undefined,
      usernamePassword: {
        username: '',
        password: {isEmbedded: false, newPassword: ''}
      }
    };

    OAuthCredentials.commonCredentialForDatasources(connections.map(c => c.id))
      .then((result: IGetCommonUserOAuthCredentialResult) => {
        let numAuthenticationTypes = 0;
        if(result.hasEmbeddedPassword) numAuthenticationTypes++;
        if(result.hasManagedKeychain)  numAuthenticationTypes++;
        if(result.hasOAuthCredential)  numAuthenticationTypes++;

        // If all connections have the same authentication type then select the proper option and fill in common details
        if(numAuthenticationTypes !== 1) {
          // TFSID:608975
          // Making NONE the default option to make clear that the data source doesn't have a valid set of credentials.
          editModel.credentialOption = 'none';
        } else {
          if(result.hasEmbeddedPassword) {
            editModel.credentialOption = 'password';
            editModel.usernamePassword = {
              username: result.commonUsername,
              password: { isEmbedded: true, newPassword: PasswordUnchanged }
            };
          } else if(result.hasManagedKeychain || result.hasOAuthCredential) {
            editModel.credentialOption = 'oauth';
            if(result.oauthCredentialId) {
              editModel.credentialId = result.oauthCredentialId;
            } else {
              // This user can see the credential but does not own it.  This displays it as an option with no value
              editModel.credentialNoValueOptionText = result.commonUsername;
            }
          }
        }
      });

    return editModel;
  }

  public updateOAuthConnections(connections: EditableConnection[], editModel: IOAuthConnectionEditModel): ng.IPromise<IActionResult> {

    let updateRequest: ng.IPromise<any>;
    switch (editModel.credentialOption) {
      case 'password':
        updateRequest = this.updateConnections(connections, editModel.usernamePassword);
        break;

      case 'oauth':
        updateRequest = this.DataConnections.updateConnectionsOAuth(connections.map(c => c.id), editModel.credentialId);
        break;

      case 'none':
        updateRequest = this.DataConnections.updateConnectionsOAuth(connections.map(c => c.id));
        break;

      default:
        throw 'Unexpected credential option: ' + editModel.credentialOption;
    }

    return updateRequest.then((result: IActionResult) => {
      this.notifyResult(result, connections);
      return result;
    });
  }

  private uniqueValueOrMixed<T>(items: T[], iterator: _.ListIterator<T, string>) {
    const values = _.map(items, iterator);
    const first = values[0];
    return _.all(values, (val) => val === first) ? first : TextBoxMixedValues;
  }

  private uniqueValueOrUndefined<T, TResult>(items: T[], iterator: _.ListIterator<T, TResult>) {
    const values = _.map(items, iterator);
    const first = values[0];
    return _.all(values, (val) => val === first) ? first : undefined;
  }

  public applyConnectionEdits(connections: EditableConnection[], editModel: IDataConnectionEditModel): ng.IPromise<IResult> {
    return this.updateConnections(connections, editModel)
      .then((result: IResult) => {
        this.notifyResult(result, connections);
        return result;
      });
  }

  private updateConnections(connections: EditableConnection[], editModel: IDataConnectionEditModel) {
    return this.DataConnections.updateConnections(connections.map(c => c.id), this.editModelToConnectionUpdate(editModel));
  }

  private editModelToConnectionUpdate(editModel: IDataConnectionEditModel): IConnectionUpdate {
    return {
      server: editModel.serverName === TextBoxMixedValues ? undefined : editModel.serverName,
      port: editModel.serverPort === TextBoxMixedValues ? undefined : editModel.serverPort,
      username: editModel.username === TextBoxMixedValues ? undefined : editModel.username,
      passwordUpdate: _.isUndefined(editModel.password.isEmbedded) ? undefined : editModel.password,
      usingRemoteQueryAgent: _.isUndefined(editModel.usingRemoteQueryAgent) ? undefined : editModel.usingRemoteQueryAgent
    };
  }

  private notifyResult(result: IResult, connections: EditableConnection[]) {

    // Edit connection does not have partial success - it is all-or-nothing
    // So, if any errors are reported, notify complete failure (with the first error message)
    if (result && result.errors && result.errors.length) {
      ContentActionNotification.displayFailureToast(this.errorMessage(result.errors[0]));
    } else {
      ContentActionNotification.displaySuccessToast(
        this.$translate.instant('updateConnectionsSuccess', {
          ITEM_COUNT: connections.length
        }, L10n.Interpolations.Count)
      );
    }
  }

  private errorMessage(error: IError) {
    switch (error.code) {
      case CommonErrorCode.DatasourceNotFound:
      case CommonErrorCode.DataConnectionNotFound:
        return this.$translate.instant('updateConnectionsError_datasourceNotFound');
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('updateConnectionsError_insufficientPermissions');
      case DataConnectionErrorCode.UpdateConnectionsInvalidPort:
        return this.$translate.instant('updateConnectionsError_invalidPort');
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

  public checkEditedConnection(connectionId: string, editModel: IDataConnectionEditModel): ITestConnectionResult {

    const testConnectionResult: ITestConnectionResult = { isPending: true };

    this.DataConnections.checkConnection(connectionId, this.editModelToConnectionUpdate(editModel)).then(
      (result: ICheckConnectionResult) => this.updateTestConnectionResult(testConnectionResult, result));

    return testConnectionResult;

  }

  private updateTestConnectionResult(
    testConnectionResult: ITestConnectionResult,
    serverResponse: ICheckConnectionResult) {

    testConnectionResult.isPending = false;
    testConnectionResult.succeeded = serverResponse.succeeded;
    testConnectionResult.messages = serverResponse.messages;
  }

  public checkOAuthConnection(oauthCredentialId: string): ITestConnectionResult {

    const testConnectionResult: ITestConnectionResult = {isPending: true};

    const handleTestResult = (success: any) => {
      testConnectionResult.succeeded = !!success;
      testConnectionResult.isPending = false;
    };

    OAuthCredentials.test(oauthCredentialId).then(handleTestResult, () => handleTestResult(false));

    return testConnectionResult;
  }

  private showNetworkType(datasource: IDataSourceBase) {
    // Don't show for Embedded datasources
    if (_.isObject(datasource.workbook)) {
      return false;
    }
    // Only show if the FF is on
    return true;
  }

}

Module.instance().service('EditConnectionAction', EditConnectionAction);
